import { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { navigateToMobileSber } from './sberLogin';

const KEY = 'mobileSberAttempt';
const EXPIRED = 'Попытка входа истекла. Начните вход через Сбер ID заново.';
export function readMobileSberAttempt() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY));
    if (/^[A-Za-z0-9_-]{43}$/.test(value?.secret) && value.expiresAt > Date.now()
        && value.expiresAt <= Date.now() + 600000) return value;
  } catch (_) { /* An interrupted/stale attempt must never restore a session. */ }
  localStorage.removeItem(KEY);
  return null;
}

export function restoreMobileSberCart() {
  const saved = readMobileSberAttempt();
  if (!saved?.cart || sessionStorage.getItem('storefrontCart') != null) return;
  try {
    if (saved.cart.length <= 65536 && Array.isArray(JSON.parse(saved.cart))) {
      sessionStorage.setItem('storefrontCart', saved.cart);
    }
  } catch (_) { /* Ignore a corrupt local cart; the server validates checkout. */ }
}

export function useMobileSberLogin({ enabled, onComplete, onError }) {
  const [attempt, setAttempt] = useState(() => enabled ? readMobileSberAttempt() : null);
  const [networkError, setNetworkError] = useState(false);
  const current = useRef(attempt);
  const callbacks = useRef({ onComplete, onError });
  callbacks.current = { onComplete, onError };
  const reset = () => {
    current.current = null;
    localStorage.removeItem(KEY);
    setAttempt(null); setNetworkError(false);
  };
  const cancel = () => {
    const previous = current.current;
    reset();
    if (previous) api.post('/client-portal/sber/mobile/cancel', { attempt_secret: previous.secret }, { skipLoading: true, timeout: 15000 })
      .catch(() => {}); // Local cancellation still wins; the server expires it.
  };
  const start = async payload => {
    const { data } = await api.post('/client-portal/sber/mobile/start', payload, { timeout: 15000 });
    if (!/^[A-Za-z0-9_-]{43}$/.test(data.attempt_secret) || data.expires_in !== 600) throw new Error('Invalid login attempt');
    const cart = sessionStorage.getItem('storefrontCart');
    const next = { secret: data.attempt_secret, expiresAt: Date.now() + data.expires_in * 1000,
      ...(cart && cart.length <= 65536 ? { cart } : {}) };
    localStorage.setItem(KEY, JSON.stringify(next));
    current.current = next;
    setAttempt(next); setNetworkError(false);
    try { navigateToMobileSber(data.launch_url); }
    catch (error) { cancel(); throw error; }
  };
  useEffect(() => {
    if (!enabled || !attempt) return undefined;
    let active = true, inFlight = false;
    const isCurrent = () => active && current.current === attempt;
    const poll = async () => {
      if (!isCurrent() || inFlight || document.visibilityState === 'hidden' || window.prokatProstAppPaused) return;
      if (Date.now() >= attempt.expiresAt) { reset(); callbacks.current.onError(EXPIRED); return; }
      inFlight = true;
      try {
        const { data } = await api.post('/client-portal/sber/mobile/complete', { attempt_secret: attempt.secret }, { skipLoading: true, timeout: 15000 });
        if (!isCurrent()) return;
        setNetworkError(false);
        if (data.status === 'completed' && typeof data.token === 'string' && data.token) {
          reset();
          callbacks.current.onComplete(data.token, data.return_to_cart === true);
        } else if (data.status === 'failed') {
          reset(); callbacks.current.onError(data.message || 'Вход не подтверждён. Попробуйте снова.');
        }
      } catch (error) {
        if (!isCurrent()) return;
        if ([401, 403, 422].includes(error.response?.status)) {
          reset(); callbacks.current.onError(EXPIRED);
        } else setNetworkError(true);
      } finally { inFlight = false; }
    };
    poll();
    const timer = setInterval(poll, 2500);
    document.addEventListener('visibilitychange', poll);
    window.addEventListener('online', poll);
    return () => {
      active = false; clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
      window.removeEventListener('online', poll);
    };
  }, [enabled, attempt]);
  return { pending: Boolean(attempt), networkError, start, cancel };
}
